import { useEffect, useRef } from 'react';
import { useAppDispatch } from '../store';
import { 
  handleNewMessage, 
  setSocketConnected, 
  updateUserStatus, 
  userStartedTyping,
  userStoppedTyping,
  messageRead 
} from '../store/chat/chatSlice';
import { setBadges, fetchBadges } from '../store/updates-hub/updatesHubSlice';
import { setClientBadges } from '../store/client-badges/clientBadgesSlice';
import { chatService } from '../services/chat.service';
import { authService } from '../services/auth.service';
import { Message, UserStatus } from '../types/chat.types';
import { BadgesState, ClientBadgesUpdatePayload } from '../types/updates-hub';
import { logSocketEvent } from '../services/socket-logger';

export const useSocket = (isAuthenticated: boolean, activeConversationId: number | null) => {
  const dispatch = useAppDispatch();
  const isConnected = useRef(false);
  const activeConversationIdRef = useRef(activeConversationId);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    if (isAuthenticated && !isConnected.current) {
      const token = authService.getAccessToken();

      if (token) {
        logSocketEvent('connect', 'global_socket_connecting');
        
        dispatch(fetchBadges());

        chatService.connect(
          token,
          () => {
            dispatch(setSocketConnected(true));
            isConnected.current = true;
            logSocketEvent('connect', 'global_socket_connected');
          },
          () => {
            dispatch(setSocketConnected(false));
            isConnected.current = false;
            logSocketEvent('disconnect', 'global_socket_disconnected');
          }
        );

        const unsubscribeFromNewMessages = chatService.onNewMessage((message: Message) => {
          logSocketEvent('on', 'new_message_received', { messageId: message.id, conversationId: message.conversationId });
          dispatch(handleNewMessage(message));
        });

        const unsubscribeFromMessageRead = chatService.onMessageRead((data: { messageId: string; readBy: number; readAt: string }) => {
          logSocketEvent('on', 'message_read_received', data);
          
          const currentActiveId = activeConversationIdRef.current;
          
          if (currentActiveId) {
             dispatch(messageRead({ 
               messageId: data.messageId, 
               conversationId: currentActiveId 
             }));
          }
        });

        const unsubscribeFromUserStatusChanged = chatService.onUserStatusChanged((data: { userId: number; status: UserStatus }) => {
          logSocketEvent('on', 'user_status_changed_received', data);
          dispatch(updateUserStatus({ userId: data.userId, status: data.status }));
        });

        const unsubscribeFromTyping = chatService.onUserTyping((data: { userName: string; conversationId: number }) => {
          logSocketEvent('on', 'user_typing_received', data);
          dispatch(userStartedTyping(data));
        });

        const unsubscribeFromStoppedTyping = chatService.onUserStoppedTyping((data: { userName: string; conversationId: number }) => {
          logSocketEvent('on', 'user_stopped_typing_received', data);
          dispatch(userStoppedTyping(data));
        });

        const unsubscribeFromBadges = chatService.onBadgesUpdate((badges: BadgesState) => {
          logSocketEvent('on', 'badges_update_received', badges);
          dispatch(setBadges(badges));
        });

        const unsubscribeFromClientBadges = chatService.onClientBadgesUpdate((payload: ClientBadgesUpdatePayload) => {
          logSocketEvent('on', 'client_badges_update_received', payload);
          dispatch(setClientBadges({ clientId: payload.clientId, badges: payload.badges }));
        });

        return () => {
          logSocketEvent('disconnect', 'global_socket_cleanup');
          unsubscribeFromNewMessages();
          unsubscribeFromMessageRead();
          unsubscribeFromUserStatusChanged();
          unsubscribeFromTyping();
          unsubscribeFromStoppedTyping();
          unsubscribeFromBadges();
          unsubscribeFromClientBadges();
          chatService.disconnect();
          dispatch(setSocketConnected(false));
          isConnected.current = false;
        };
      }
    } else if (!isAuthenticated && isConnected.current) {
      chatService.disconnect();
      dispatch(setSocketConnected(false));
      isConnected.current = false;
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (isAuthenticated && activeConversationId) {
      logSocketEvent('emit', 'joining_conversation_room', { conversationId: activeConversationId });
      chatService.joinConversation(activeConversationId);

      return () => {
        logSocketEvent('emit', 'leaving_conversation_room', { conversationId: activeConversationId });
        chatService.leaveConversation(activeConversationId);
      };
    }
  }, [isAuthenticated, activeConversationId]);
};
